import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, effect, input, viewChild } from '@angular/core';
import { InavistaDropdown } from '@inavista/components/dropdown';
import { Checkbox as InavistaCheckbox, DatePicker as InavistaDatePicker, TextBox as InavistaTextBox } from '@inavista/components/ui';
import { CellEdit, CellEditor, DateTimeEditor, DropdownEditor, NumberEditor } from './cell-editors';

/**
 * Draws a column's cell editor (see cell-editors.ts) inside an @inavista/components grid editor template, with the
 * library's own controls. While the cell is open the editor is in `registry` under its field; the page reads every
 * open editor with `read()` when the row is saved.
 *
 * <ng-template #ed let-row><app-cell-editor [edit]="edits.qty" field="qty" [rowData]="rowFor(row)" [registry]="open" /></ng-template>
 */
@Component({
  selector: 'app-cell-editor',
  standalone: true,
  imports: [InavistaDropdown, InavistaTextBox, InavistaDatePicker, InavistaCheckbox],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (editor; as e) {
      @switch (e.kind) {
        @case ('dropdown') {
          <span class="cell-editor-row">
            <inavista-dropdown #dropdown class="cell-editor-control" [options]="dd.options()" [value]="dd.value" [selectedLabel]="dd.selectedLabel()"
              [showSearch]="dd.allowFiltering" [remoteSearch]="!!dd.onFiltering && !dd.filterLocally" [loading]="dd.loading()" [disabled]="!dd.isEnabled()"
              [panelWidth]="dd.popupWidth" [listOverlay]="true" listPosition="auto" [clearable]="dd.clearable" [openOnFocus]="dd.openOnFocus" [placeholder]="dd.placeholder"
              (valueChange)="dd.setValue($event, true)" (searchChange)="dd.onFiltering?.({ text: $event })"
              (focusChange)="$event && dd.onFocus?.()" />
            @if (dd.action && dd.actionVisible()) {
              <button type="button" class="cell-editor-action" [title]="dd.action.title" [attr.aria-label]="dd.action.title"
                (click)="dd.action.click()"><i [class]="dd.action.iconClass"></i></button>
            }
          </span>
        }
        @case ('number') {
          <span class="cell-editor-row" [title]="num.hint?.() ?? ''">
            <inavista-textbox #numberBox class="cell-editor-control" type="number" [value]="num.value" [decimals]="num.shownDecimals()"
              [min]="num.min" [max]="num.max" [step]="num.step" [spinButtons]="false" [disabled]="!num.isEnabled()"
              (valueChange)="onNumberTyped($event)" (committed)="onNumberCommitted($event)" (focusChange)="$event && num.onFocus?.()" />
          </span>
        }
        @case ('datetime') {
          <inavista-date-picker class="cell-editor-control" mode="datetime" [value]="dt.value" [min]="dt.min" [max]="dt.max"
            [allowEdit]="dt.allowEdit" [openOnFocus]="dt.openOnFocus" [placeholder]="dt.placeholder" [disabled]="!dt.isEnabled()"
            (valueChange)="dt.setValue($event, true)" />
        }
        @case ('checkbox') {
          <inavista-checkbox [checked]="e.value" [disabled]="!e.isEnabled()" (checkedChange)="e.setValue($event, true)" />
        }
        @default {
          <inavista-textbox class="cell-editor-control" [value]="e.value" [placeholder]="e.placeholder" [disabled]="!e.isEnabled()"
            (valueChange)="e.setValue($event, true)" (focusChange)="$event && e.onFocus?.()" />
        }
      }
    }
  `,
  styles: [`
    :host { display: block; width: 100%; }
    .cell-editor-row { display: flex; align-items: center; gap: 2px; width: 100%; }
    .cell-editor-control { flex: 1 1 auto; min-width: 0; width: 100%; }
    /* A list fills its cell, as the old one did (the dropdown's own width is 10rem otherwise, even when empty). */
    inavista-dropdown.cell-editor-control { --inavista-dropdown-width: 100%; --inavista-dropdown-min-width: 0; }
    .cell-editor-action { flex: 0 0 auto; border: 0; background: transparent; padding: 0 4px; cursor: pointer; color: inherit; }
  `],
})
export class CellEditorHost implements OnInit, OnDestroy {
  readonly edit = input.required<CellEdit>();
  readonly field = input.required<string>();
  readonly rowData = input<any>();
  readonly registry = input.required<Map<string, CellEdit>>();

  private readonly dropdown = viewChild<InavistaDropdown<any>>('dropdown');
  private readonly numberBox = viewChild<InavistaTextBox>('numberBox');
  editor: CellEditor | null = null;
  private typingTimer: any = null;

  constructor() {
    // The Item column's focus opened its list (showPopup): the drawn dropdown's openPanel.
    effect(() => {
      const dropdown = this.dropdown();
      if (this.editor instanceof DropdownEditor) this.editor.attachPanel(dropdown ? () => dropdown.openPanel() : null, dropdown ? () => dropdown.focus() : null);
    });
  }

  get dd() { return this.editor as DropdownEditor; }
  get num() { return this.editor as NumberEditor; }
  get dt() { return this.editor as DateTimeEditor; }

  ngOnInit() {
    const edit = this.edit();
    this.editor = edit.open({ rowData: this.rowData(), column: { field: this.field() } });
    this.registry().set(this.field(), edit);
  }

  // Typing is applied as the old keyup handlers did (each key, or after the editor's pause); leaving the box applies it.
  onNumberTyped(value: any) {
    const editor = this.num;
    clearTimeout(this.typingTimer);
    if (!editor.typingDelay) editor.setValue(value, true);
    else this.typingTimer = setTimeout(() => editor.setValue(value, true), editor.typingDelay);
  }

  // Leaving the box shows the kept value, as Syncfusion's strict mode did (99999 typed over a max of 2540 shows 2540).
  // The binding alone can't: when the kept value equals the last one bound, Angular writes nothing.
  onNumberCommitted(value: any) {
    clearTimeout(this.typingTimer);
    this.num.setValue(value, true);
    const box = this.numberBox();
    if (box && box.value() !== this.num.value) box.value.set(this.num.value);
  }

  ngOnDestroy() {
    clearTimeout(this.typingTimer);
    if (this.editor instanceof DropdownEditor) this.editor.attachPanel(null);
    if (this.registry().get(this.field()) === this.edit()) this.registry().delete(this.field());
    this.edit().destroy?.();
  }
}
